<template>
  <div class="daily-spend-limit-overlay">
    <p class="small">
      Set the daily spend limit for this card.
    </p>
    <div v-if="card.Holder">
      <label>Card</label>
      <br />
      *{{ card.CardNumber }} - {{ holderDisplay }}
    </div>
    <br />
    <!-- PRODUCTDEV-5946: the field mirrors the PEX business settings fetched on open (getDailySpendLimit):
         :required hides ed-input's automatic "- optional" label suffix when the account requires a limit,
         the help-text shows the account's real cap ("Max limit 5,000.00 USD" - PRODUCTDEV-5573 thread), and
         has-error paints the red border while the message below shows the specific rule that failed. -->
    <div class="form-group" :class="{ 'has-error': !!validationError }">
      <ed-input type="money"
                amount
                v-model="DailySpendLimit"
                @input="validateDailySpendLimit"
                :required="isDailySpendLimitRequired"
                :help-text="maxLimitHelpText"
                field-label="Daily Spend Limit" />
    </div>
    <!-- PRODUCTDEV-5890 / 5898 / 5946 / 5889: inline validation against the PEX business settings
         (required flag + per-account cap - no guessed defaults; unknown cap = no cap pre-validation).
         Reject an invalid value here before calling the backend (which would make a PEX call that PEX is
         certain to reject). A blank value is allowed only when the account does not require a limit;
         $0.00 is always rejected and PEX remains the enforcement authority on save. -->

    <p v-if="validationError" class="dsl-validation-error">{{ validationError }}</p>
  </div>
</template>

<script>

import { EventBus } from '@/event-bus'

// PRODUCTDEV-5946: "5000" -> "5,000.00" for the "Max limit 5,000.00 USD" help text and the
// less-than-or-equal validation message. Module-scope (not a method) so validateDailySpendLimit stays a
// self-contained value -> result check (the unit specs call it with a bare context object).
const formatUsdAmount = (value) => Number(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

export default {

  created() {
    this.getDailySpendLimit()
  },

  computed: {
    // PRODUCTDEV-5884: surface the custom Name on Card next to the legal holder
    // ("Name on Card | Legal Name") when set and meaningfully different, mirroring CardRefill.vue
    // and the cards grid (see PRODUCTDEV-5553). Holder may be a plain string or an object with .Name.
    holderDisplay() {
      const legalName = this.card && typeof this.card.Holder === 'object'
        ? (this.card.Holder.Name || '')
        : ((this.card && this.card.Holder) || '')
      const nameOnCard = ((this.card && this.card.NameOnCard) || '').trim()
      if (!nameOnCard || nameOnCard.toLowerCase() === legalName.trim().toLowerCase()) {
        return legalName
      }
      if (!legalName.trim()) {
        return nameOnCard
      }
      return `${nameOnCard} | ${legalName}`
    },

    // PRODUCTDEV-5946: the "Max limit 5,000.00 USD" help text under the field (wording per the
    // PRODUCTDEV-5573 thread), driven by the PEX per-account cap fetched on open. PRODUCTDEV-5889:
    // no cap known -> no help text (never show a guessed number).
    maxLimitHelpText() {
      return this.maxDailySpendLimit ? `Max limit ${formatUsdAmount(this.maxDailySpendLimit)} USD` : ''
    }
  },

  methods: {
    notifyError(message) {
      this.$notifications.notify({
        alert: true,
        type: 'plain',
        icon: 'exclamation-circle',
        placement: { from: 'top', align: 'center' },
        timeout: 0,
        message: message
      })
    },

    // PRODUCTDEV-5890/5946: inline-validate against the PEX business settings before calling the backend.
    // A blank value (None - the limit clears) is allowed only when the account does not require a limit;
    // $0.00 and above-cap values are rejected with the ticket's messages. Returns true when ok to submit.
    // PRODUCTDEV-5890 (PR #36368 review): validate the passed-in value - the @input payload while typing, or
    // this.DailySpendLimit from save() - rather than driving validation off a watcher. The watcher also fired
    // on the programmatic load in getDailySpendLimit(), so a card whose stored limit legitimately exceeds
    // $5,000 (PEX can raise the cap per business) showed a spurious error the instant the modal opened.
    validateDailySpendLimit(value) {
      const amount = (value === '' || value == null) ? null : +value
      if (amount == null) {
        if (this.isDailySpendLimitRequired) {
          this.validationError = 'Daily Spend Limit is required.'
          return false
        }
      } else if (amount <= 0) {
        this.validationError = 'Daily Spend Limit must be greater than $0.00'
        return false
      } else if (this.maxDailySpendLimit && amount > this.maxDailySpendLimit) {
        // PRODUCTDEV-5889: the cap check only runs when the backend reported a real cap - no guessed
        // $5,000 anymore. Without one, PEX validates the amount on save and its error is surfaced.
        this.validationError = `Daily Spend Limit must be less than or equal to $${formatUsdAmount(this.maxDailySpendLimit)}`
        return false
      }
      this.validationError = ''
      return true
    },

    // EvedPay card endpoints return a bare array of { Message, StatusCode } (see GetStatusMappedResult).
    // A thrown 4xx carries it on error.response.data; a resolved non-2xx carries it on response.data.
    extractErrorMessage(errorOrResponse) {
      const data = (errorOrResponse && errorOrResponse.response && errorOrResponse.response.data) ||
        (errorOrResponse && errorOrResponse.data)
      if (data && data.length && data[0] && data[0].Message) {
        return `Daily Spend Limit could not be updated. ${data[0].Message}`
      }
      return ''
    },

    async save(showModal) {
      if (this.card.Id) {
        // PRODUCTDEV-5890: stop here (no API / PEX call) when the value fails inline validation.
        if (!this.validateDailySpendLimit(this.DailySpendLimit)) {
          return
        }
        try {
          // PRODUCTDEV-5884 (PR #36348 review): always send the DailySpendLimit key so intent is explicit -
          // null clears it (backend treats null as None), and a number is sent as entered. A typed 0 is sent
          // and the backend rejects it (clean error, previous value kept); a blank field clears the limit.
          // (Omitting the key relied on PATCH "absent = unchanged" binding and silently dropped a typed 0.)
          const response = await this.api.patch(`evedpaycards/${this.card.Id}/daily-spend-limit`, {
            DailySpendLimit: (this.DailySpendLimit === '' || this.DailySpendLimit == null) ? null : +this.DailySpendLimit,
            // PRODUCTDEV-5890: suppress the global generic "Something went wrong" error toast (the axios
            // interceptor honors preventError on the request body) so the specific PEX/backend message shown
            // below is the only error the user sees. The backend ignores this extra key.
            preventError: true
          })
          if (response.status === 200) {
            if (showModal) {
              showModal.showModal = false
            }
            // PRODUCTDEV-5908: emit the bare card id, exactly like CardRefill ('refill') / CardStatus
            // ('status-changed') / CardSettings ('funds-changed'). The extended event bus wraps the payload as
            // { target, data: <id> }, so the index.vue listener reads cardId.data. (Previously this passed
            // { data: id }, which got double-wrapped; the PRODUCTDEV-5884 unwrap loop in getCardWithDetails
            // masked it - this keeps the emit consistent with the other three approval sections.)
            EventBus.$emit('dailySpendLimit', this.card.Id)
          } else {
            // PRODUCTDEV-5890: a non-success response (e.g. PEX rejected the value). Show the specific reason
            // and do NOT emit - the tile keeps the previously-saved value so Eved stays in sync with PEX.
            this.notifyError(this.extractErrorMessage(response) || 'Failed to save the Daily Spend Limit. Please try again.')
          }
        } catch (error) {
          console.error(error)
          // PRODUCTDEV-5890: PEX (or the backend) rejected the change. Surface the specific reason and do NOT
          // emit, so the displayed Daily Spend Limit stays at the previously-saved value (Eved in sync with PEX).
          this.notifyError(this.extractErrorMessage(error) || 'Failed to save the Daily Spend Limit. Please try again.')
        }
      }
    },

    async getDailySpendLimit() {
      if (this.card.Id) {
        try {
          const response = await this.api.get(`evedpaycards/${this.card.Id}/daily-spend-limit`)
          if (response.status === 200 && response.data) {
            this.DailySpendLimit = response.data.DailySpendLimit
            // PRODUCTDEV-5898/5889: the per-account cap is PEX-controlled; use the backend value for
            // validation. Only accept a positive, finite number - a missing field (NaN), a non-numeric
            // value, 0 or a negative leave the cap at null (no pre-validation, no help text; PEX enforces
            // the real cap on save). No $5,000 guess anymore - a guessed number can be wrong both ways.
            const cap = +response.data.MaxDailySpendLimit
            if (Number.isFinite(cap) && cap > 0) {
              this.maxDailySpendLimit = cap
            }
            // PRODUCTDEV-5946: whether the account's PEX business settings require a Daily Spend Limit
            // on every card. Strict === true so an absent field keeps the optional default.
            this.isDailySpendLimitRequired = response.data.IsDailySpendLimitRequired === true
          }
        } catch (error) {
          console.error(error)
          this.notifyError('Failed to load the Daily Spend Limit.')
        }
      }
    }
  },

  props: {
    card: Object
  },

  data() {
    return {
      DailySpendLimit: null,
      validationError: '',
      // PRODUCTDEV-5898/5889: the validation cap, sourced from the backend per account. null = no cap
      // known (still loading, or PEX reported none) - NO $5,000 guess anymore; the cap pre-validation and
      // the "Max limit" help text are simply skipped and PEX enforces the real cap on save (its error is
      // surfaced by save()).
      maxDailySpendLimit: null,
      // PRODUCTDEV-5946: whether the PEX business settings require a Daily Spend Limit on every card.
      // Optional until the fetched value says otherwise, so the field is never over-restricted while loading.
      isDailySpendLimitRequired: false
    }
  }
}
</script>

<style lang="scss" scoped>
  .daily-spend-limit-overlay {
    background-color: white;
    padding: 20px;
    border: 1px solid #ccc;
    border-radius: 4px;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
  }

  .dsl-validation-error {
    color: #E70404;
    font-size: 0.85em;
    margin-top: 6px;
    margin-bottom: 0;
  }
</style>



// WEBPACK FOOTER //
// src/components/_modules/Programs/Card-list/CardDailySpendLimit.vue